import Link from 'next/link'
import { getJournalData } from '~shared/api/single-journal-data/single-journal-data'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'
import { cn } from '~shared/utils/cn'

// ─── Types ────────────────────────────────────────────────────────────────────

type BreadcrumbItem = {
  label: string
  href?: string
}

type Props = {
  // Режим 1: страница программы
  program?: Awaited<ReturnType<typeof getProgramData>>
  journal?: Awaited<ReturnType<typeof getJournalData>>
  // Режим 2: страница списка программ (с факультетом или без)
  categoryName?: string | null
  categorySlug?: string | null
  facultyName?: string | null
  facultySlug?: string | null
  // Режим 3: простые крошки (передаёшь массив напрямую)
  items?: BreadcrumbItem[]
  className?: string
}

// ─── Component ────────────────────────────────────────────────────────────────

export const Breadcrumbs = ({
  program,
  categoryName,
  categorySlug,
  facultyName,
  facultySlug,
  journal,
  items: itemsProp,
  className
}: Props) => {
  // ─── Режим 1: program передан ─────────────────────────────────────────────
  const programItems: BreadcrumbItem[] | null = program
    ? [
        { label: 'Главная', href: '/' },
        {
          label: program?.category?.name || '',
          href: `/${program?.category?.slug}`
        },
        {
          label: program?.faculty?.name || '',
          href: `/${program?.category?.slug}--fakultet--${program?.faculty?.slug}`
        },
        { label: program?.name || '' }
      ].filter(item => item.label)
    : null

  // ─── Режим 2: строим из categoryName/facultyName ──────────────────────────
  const listItems: BreadcrumbItem[] | null =
    !program && !itemsProp && !journal
      ? [
          { label: 'Главная', href: '/' },
          ...(categoryName && categorySlug
            ? [{ label: categoryName, href: `/${categorySlug}` }]
            : []),
          ...(facultyName && facultySlug && categorySlug
            ? [
                {
                  label: facultyName,
                  href: `/${categorySlug}--fakultet--${facultySlug}`
                }
              ]
            : [])
        ].filter(item => item.label)
      : null

  // ─── Режим 3: готовый массив крошек ───────────────────────────────────────
  const simpleItems: BreadcrumbItem[] | null = itemsProp
    ? [{ label: 'Главная', href: '/' }, ...itemsProp].filter(item => item.label)
    : null

  // ─── Режим 4: journal передан ───────────────────────────────────────
  const journalItems: BreadcrumbItem[] | null = journal
    ? [
        { label: 'Главная', href: '/' },
        { label: 'Журнал', href: '/journal' },
        {
          label: journal?.tag?.name || '',
          href: `/journal--${journal?.tag?.slug}`
        },
        { label: journal?.name || '' }
      ].filter(item => item.label)
    : null

  const items = programItems || simpleItems || journalItems || listItems || []

  if (items.length === 0) return null

  const backLink = items[items.length - 2]
  const currentLabel = items[items.length - 1]?.label

  return (
    <nav
      className={cn(
        'mx-auto px-[16px] box-border max-w-[672px] md:max-w-[832px] 2lg:max-w-[1280px] mb-[40px]',
        className
      )}
      aria-label='breadcrumbs'>
      {/* ─── Mobile ─── */}
      <div className='flex flex-col gap-[16px] md:hidden'>
        {backLink?.href && (
          <Link
            href={backLink.href}
            className='text-[14px] text-gray-500 flex items-center gap-1'>
            ← {backLink.label}
          </Link>
        )}
        <span className='text-[16px] font-medium text-black truncate'>
          {currentLabel}
        </span>
      </div>

      {/* ─── Desktop ─── */}
      <div className='hidden md:block'>
        <ul className='flex flex-wrap items-center gap-2 text-[#A0A0A0]'>
          {items.map((item, index) => {
            const isLast = index === items.length - 1
            return (
              <li key={index} className='flex items-center gap-2'>
                {item.href && !isLast ? (
                  <Link
                    href={item.href}
                    className='hover:text-black transition-colors text-[14px] md:text-[16px]'>
                    {item.label}
                  </Link>
                ) : (
                  <span className='text-black text-[14px] md:text-[16px]'>
                    {item.label}
                  </span>
                )}
                {!isLast && (
                  <svg
                    width='7'
                    height='10'
                    viewBox='0 0 7 10'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M1.81934 9L5.81934 5L1.81934 1'
                      stroke='#8B8B8B'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </nav>
  )
}
